/**
 * AbilityCaptureTab — automated sweep captures and montage review.
 *
 * Modernized progress meter, diagnostic card layout, and action buttons.
 */
import React from 'react';
import { Button, Card, ProgressBar, Badge } from '../../primitives';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  PLAYER_UI,
  RADII,
  SPACE,
} from '../../theme';
import { type CaptureSweepState, type LabState } from './types';
import { type AbilityCaptureStatus } from '../../../diagnostics/AbilityCaptureStatus';

interface AbilityCaptureTabProps {
  labState: LabState;
  selectedAbility: string;
  selectedAbilityName?: string;
  sweep: CaptureSweepState;
  sweepTargetsCount: number;
  captureReceipt: AbilityCaptureStatus | null;
  runCaptureSweep: () => Promise<void>;
  reviewCaptures: () => void;
}

export const AbilityCaptureTab: React.FC<AbilityCaptureTabProps> = ({
  labState,
  selectedAbility,
  selectedAbilityName,
  sweep,
  sweepTargetsCount,
  captureReceipt,
  runCaptureSweep,
  reviewCaptures,
}) => {
  return (
    <div style={styles.container}>
      <Card
        title="Capture Automation"
        subtitle={
          selectedAbilityName
            ? `Deterministic views for ${selectedAbilityName} · render-truth grading`
            : 'Completion-driven views · render-truth grading · no fixed sleeps'
        }
        icon="🎞"
      >
        {/* Metric tiles */}
        <div style={styles.metricsRow}>
          <div style={styles.metricTile}>
            <span style={styles.metricKicker}>VIEW SET</span>
            <strong style={styles.metricVal}>
              {sweepTargetsCount === 1 ? 'Self (1 view)' : '4 Authored Targets'}
            </strong>
          </div>
          <div style={styles.metricTile}>
            <span style={styles.metricKicker}>LATEST RUNTIME</span>
            <strong style={styles.metricVal}>
              {captureReceipt?.runtimeMs != null
                ? `${(captureReceipt.runtimeMs / 1000).toFixed(2)}s`
                : '—'}
            </strong>
          </div>
        </div>

        {/* Glossed Progress Bar */}
        <div style={{ marginTop: SPACE.xs, marginBottom: SPACE.xs }}>
          <ProgressBar
            value={sweep.completed}
            max={Math.max(1, sweep.total)}
            color="xp"
            glossed={true}
            height={8}
            label={
              <div style={styles.progressLabel}>
                <span>Sweep Progress</span>
                <span style={styles.progressPct}>
                  {sweep.total > 0 ? `${Math.round((sweep.completed / sweep.total) * 100)}%` : 'Idle'}
                </span>
              </div>
            }
          />
        </div>

        {/* Capture receipt readout */}
        <div style={styles.captureReceiptLine} data-testid="grounds-capture-receipt">
          {sweep.running ? (
            <span>
              <strong style={{ color: PLAYER_UI.brassBright }}>{sweep.completed}/{sweep.total}</strong> stored · {sweep.current ?? 'starting'}{sweep.current ? ' in progress' : ''}
            </span>
          ) : captureReceipt?.state === 'complete' ? (
            <span>
              {captureReceipt.uploadedFrames}/{captureReceipt.expectedFrames} frames · {captureReceipt.eventFrames} event beats · <code style={styles.castIdCode}>{captureReceipt.castId}</code>
            </span>
          ) : (
            'A successful cast automatically creates a timed frame burst.'
          )}
        </div>

        {sweep.error && (
          <div style={styles.captureError}>
            <Badge tone="danger" size="sm">Error</Badge> {sweep.error}
          </div>
        )}

        {/* Action buttons */}
        <div style={styles.captureActions}>
          <Button
            size="sm"
            fullWidth
            variant={sweep.running ? 'danger' : 'brass'}
            disabled={labState !== 'ready' || !selectedAbility}
            onClick={() => { void runCaptureSweep(); }}
            data-testid="grounds-capture-sweep"
          >
            {sweep.running
              ? 'Stop after this view'
              : `Capture ${sweepTargetsCount} view${sweepTargetsCount === 1 ? '' : 's'}`}
          </Button>
          <Button
            size="sm"
            fullWidth
            variant="secondary"
            disabled={!selectedAbility || sweep.running}
            onClick={reviewCaptures}
            data-testid="grounds-review-captures"
          >
            Open montage review
          </Button>
        </div>
      </Card>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.sm,
  },
  metricsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACE.xs,
  },
  metricTile: {
    padding: `${SPACE.xs}px ${SPACE.sm}px`,
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: RADII.sm,
    border: `1px solid ${COLORS.borderSubtle}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  metricKicker: {
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: COLORS.textMuted,
  },
  metricVal: {
    fontSize: FONT_SIZE.sm,
    color: COLORS.textPrimary,
    fontFamily: FONTS.mono,
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: FONT_SIZE.xs,
    color: COLORS.textSecondary,
    marginBottom: 2,
  },
  progressPct: {
    fontFamily: FONTS.mono,
    fontWeight: FONT_WEIGHT.bold,
    color: PLAYER_UI.brassBright,
  },
  captureReceiptLine: {
    padding: `${SPACE.xxs}px ${SPACE.xs}px`,
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: RADII.sm,
    color: COLORS.textSecondary,
    fontFamily: FONTS.mono,
    fontSize: FONT_SIZE.xs,
    lineHeight: 1.45,
    overflowWrap: 'anywhere',
    border: '1px solid rgba(255, 255, 255, 0.04)',
  },
  castIdCode: {
    color: PLAYER_UI.brassBright,
    fontSize: 10,
  },
  captureError: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.xs,
    color: COLORS.danger,
    fontSize: FONT_SIZE.xs,
    marginTop: SPACE.xs,
  },
  captureActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: SPACE.xs,
    marginTop: SPACE.sm,
  },
};
